/**
 * 오늘 상태 알림 띠 (점주 화면 공통 상단)
 *
 * 평소에는 아무것도 안 보이고, 아래 경우에만 한 줄로 뜬다:
 *   - 오늘이 공휴일(빨간날) → 하루 전체 자율학습
 *   - 오늘이 일요일        → 하루 전체 자율학습
 *   - 오늘 수능·모의고사 등 주요 일정이 있는 날
 *
 * "빨간날이다"만 알리는 게 아니라 "그래서 오늘 어떻게 돌아가는지"까지 같이 보여준다.
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';

interface TodaySummary {
  date: string;
  weekday: string;
  is_holiday: boolean;
  holiday_name: string | null;
  is_sunday: boolean;
  all_day_self_study: boolean;
  regular_deadline: string | null;
  self_study_start: string | null;
  self_study_end: string | null;
  events: Array<{ category: string; title: string }>;
}

const TodayNoticeBanner: React.FC = () => {
  const [summary, setSummary] = useState<TodaySummary | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    fetch(`${API_BASE_URL}/api/v1/owner/calendar/today`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setSummary(data))
      .catch(() => setSummary(null));
  }, []);

  if (!summary) return null;

  const majorEvents = summary.events.filter(
    (e) => e.category === 'exam' || e.category === 'academic'
  );
  const isRedDay = summary.is_holiday || summary.is_sunday;

  if (!isRedDay && majorEvents.length === 0) return null;

  const dayLabel = summary.is_holiday
    ? `오늘은 공휴일입니다${summary.holiday_name ? ` (${summary.holiday_name})` : ''}`
    : summary.is_sunday
    ? '오늘은 일요일입니다'
    : '오늘 일정';

  const operationLabel = summary.all_day_self_study
    ? `하루 전체 자율학습${
        summary.self_study_start && summary.self_study_end
          ? ` (${summary.self_study_start} ~ ${summary.self_study_end})`
          : ''
      } · 벌점은 새벽 ${summary.self_study_end || '02:00'}에 매겨집니다`
    : summary.self_study_start
    ? `${summary.self_study_start}부터 자율학습`
    : null;

  return (
    <div
      className={`px-4 py-2 text-sm border-b ${
        isRedDay
          ? 'bg-rose-50 border-rose-200 text-rose-800'
          : 'bg-violet-50 border-violet-200 text-violet-800'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {isRedDay && <span aria-hidden="true">🔴</span>}
        <strong>{dayLabel}</strong>
        {isRedDay && operationLabel && (
          <>
            <span className="opacity-60">—</span>
            <span>{operationLabel}</span>
          </>
        )}
        {majorEvents.map((e, i) => (
          <span
            key={`${e.title}-${i}`}
            className="px-1.5 py-0.5 text-xs rounded bg-white/70 border"
          >
            {e.title}
          </span>
        ))}
      </div>
    </div>
  );
};

export default TodayNoticeBanner;
